import { useState, useEffect, useCallback } from 'react';

import useClient from 'api/client';
import queryGetDatasetShareRequest from 'api/ShareObject/queryGetDatasetShareRequest';
import queryGetTableShareRequest from 'api/ShareObject/queryGetTableShareRequest';
import queryGetEnvShareRequest from 'api/ShareObject/queryGetEnvShareRequest';
import { DataType, Role, ShareRequestItem } from 'types/Share';
import getEnvironmentSalmGroups from 'api/Environment/getEnvironmentSamlGroups';

interface useResourceEnvironmentsProps {
    dataType?: DataType;
    resourceUri: string;
}

export default function useResourceEnvironments({
    dataType,
    resourceUri,
}: useResourceEnvironmentsProps) {
    const client = useClient();
    const [resource, setResource] = useState<ShareRequestItem>(
        {} as ShareRequestItem,
    );
    const [environmentError, setEnvironmentError] = useState<string | null>(
        null,
    );
    const [environmentOptions, setEnvironmentOptions] = useState([]);
    const [loading, setLoading] = useState<boolean>(true);

    const [roleOptions, setRoleOptions] = useState<Role[]>([]);

    const fetchItems = useCallback(async () => {
        let response;
        if (dataType === 'dataset') {
            response = await client.query(
                queryGetDatasetShareRequest(resourceUri),
            );
            if (!response.errors) {
                setResource(response.data.getDataset);
            }
        } else if (dataType === 'table') {
            response = await client.query(
                queryGetTableShareRequest(resourceUri),
            );
            if (!response.errors) {
                setResource(response.data.getDatasetTable);
            }
        }

        if (response?.errors) {
            console.error(
                `Something went wrong fetching dataset share request. ${response.errors[0]?.message}`,
            );
        }
    }, [client]);

    const fetchEnvironments = useCallback(async () => {
        setLoading(true);
        const response = await client.query(
            queryGetEnvShareRequest({
                filter: {
                    roles: ['Admin', 'Owner', 'Invited', 'DatasetCreator'],
                    page: 1,
                    pageSize: 200,
                    onlyProduction: true,
                },
            }),
        );
        if (!response.errors) {
            setEnvironmentOptions(
                response.data.listEnvironments.nodes.map((env: any) => {
                    return {
                        name: env.environmentUri,
                        label: `${env.label}`,
                        prodAccount: env.AwsAccountId,
                        devAccount: env.AwsDevAccountId,
                        testAccount: env.AwsTestAccountId,
                        valAccount: env.AwsValAccountId,
                    };
                }),
            );
        } else {
            setEnvironmentError(`Error fetching environments`);
        }
    }, [client]);

    useEffect(() => {
        if (client) {
            setLoading(true);
            Promise.all([fetchItems(), fetchEnvironments()]).then(() => {
                setLoading(false);
            });
        }
    }, [client, fetchEnvironments, fetchItems]);

    async function fetchRoles(environmentUri: string) {
        const response = await client?.query(
            getEnvironmentSalmGroups(environmentUri),
        );

        if (!response.errors) {
            const combinedGroups = [].concat(
                response.data.getEnvironment.originSamlGroupName,
                response.data.getEnvironment.originSamlBasicDevGroupName,
                response.data.getEnvironment.originSamlFullDevGroupName,
                response.data.getEnvironment.originSamlReaderGroupName,
            );

            setRoleOptions(
                combinedGroups.map((value: string) => ({
                    value: value?.toLowerCase(), // BE lowercase the value of 'Saml...GroupName' (non origin)
                    label: value, // origin group name
                })),
            );
        } else {
            setEnvironmentError('Could not fetch roles');
        }
    }
    return {
        environmentError,
        environmentOptions,
        loading,
        resource,
        setEnvironmentError,
        roleOptions,
        setRoleOptions,
        fetchRoles,
    };
}
